'use client';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
  Image,
} from '@react-pdf/renderer';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

const makeBarcode = typeof JsBarcode === 'function'
  ? JsBarcode
  : JsBarcode?.default;

const styles = StyleSheet.create({
  page: {
    padding: '20 15',
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'column',
    marginBottom: 10,
  },
  headerItem: {
    fontSize: 9,
    marginRight: 20,
  },
  mainContent: {
    flexDirection: 'row',
  },
  leftSection: {
    flex: 1,
    marginRight: 15,
  },
  rightSection: {
    flex: 1,
  },
  table: {
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#000',
  },
  tableCell: {
    flex: 1,
    padding: 3,
    borderRightWidth: 1,
    borderColor: '#000',
    textAlign: 'center',
    fontSize: 7,
  },
  tableCellDate: {
    flex: 1.5,
    padding: 3,
    borderRightWidth: 1,
    borderColor: '#000',
    textAlign: 'center',
    fontSize: 7,
  },
  tableCellLast: {
    flex: 1,
    padding: 5,
    textAlign: 'center',
    fontSize: 7,
  },
  sectionTitle: {
    padding: 4,
    marginBottom: 4,
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 'bold',
  },
  gridContainer: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  gridItem: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#000',
    padding: 2,
  },
  barcodeSection: {
    marginTop: 8,
    alignItems: 'center',
  },
  barcodeBox: {
    borderWidth: 1,
    borderColor: '#000',
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  barcodeImage: {
    width: 200,
    height: 80,
  },
  barcodeLabel: {
    marginTop: 6,
    fontSize: 10,
    textAlign: 'center',
  },
});

const PDFContent = ({ data, date, barcode }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.mainContent}>
        {/* Left Section */}
        <View style={styles.leftSection}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerItem}>
              <Text>Card No. {data.cardNo}</Text>
            </View>
            <View style={styles.headerItem}>
              <Text>Drg. No. {data.drgNo === "Other" ? data.customDrgNo : data.drgNo}</Text>
            </View>
            <View style={{ flexDirection: 'row' }}>
            <View style={styles.headerItem}>
              <Text>Date {data.date ? String(data.date).split('T')[0].split('-').reverse().join('-') : ''}</Text>
            </View>
              <View style={styles.headerItem}>
                <Text>M/c No. {data.mcNo}</Text>
              </View>
            </View>
          </View>
          {/* Type and Details Table */}
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Type</Text>
              <Text style={styles.tableCell}>d</Text>
              <Text style={styles.tableCell}>OD/ID</Text>
              <Text style={styles.tableCell}>L</Text>
              <Text style={styles.tableCell}>T. Coils</Text>
              <Text style={styles.tableCell}>Active Coils</Text>
              <Text style={styles.tableCellLast}>Grinding</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>O/s</Text>
              <Text style={styles.tableCell}>{data.osd}</Text>
              <Text style={styles.tableCell}>{data.osodId}</Text>
              <Text style={styles.tableCell}>{data.osl}</Text>
              <Text style={styles.tableCell}>{data.ostColls}</Text>
              <Text style={styles.tableCell}>{data.osactiveColls}</Text>
              <Text style={styles.tableCellLast}>{data.osgrinding}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>M/c.</Text>
              <Text style={styles.tableCell}>{data.mcd}</Text>
              <Text style={styles.tableCell}>{data.mcodId}</Text>
              <Text style={styles.tableCell}>{data.mcl}</Text>
              <Text style={styles.tableCell}>{data.mctColls}</Text>
              <Text style={styles.tableCell}>{data.mcactiveColls}</Text>
              <Text style={styles.tableCellLast}>{data.mcgrinding}</Text>
            </View>
          </View>

          {/* Production Details */}
          <View style={{ flexDirection: 'row' }}>
            <View style={[styles.leftSection, { marginRight: 10 }]}>
              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Quantity</Text>
                  <Text style={styles.tableCell}>{data.quantity} Nos.</Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>W.T. Reqd.</Text>
                  <Text style={styles.tableCell}>{data.wtReqd} Kg</Text>
                </View>
              </View>
            </View>
            <View style={styles.rightSection}>
              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Estimate</Text>
                  <Text style={styles.tableCell}>{data.estimateQty}/{data.estimateWt}</Text>
                  <View style={{ width: 41.5, alignItems: 'center', paddingVertical: 4 }}>
                    <Text style={{ fontSize: 6 }}>Pcs.</Text>
                    <View style={{ width: 20, height: 1, backgroundColor: 'black', marginVertical: 4, alignSelf: 'center' }} />
                    <Text style={{ fontSize: 6 }}>gm.</Text>
                  </View>
                </View>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Rate</Text>
                  <Text style={styles.tableCell}>{data.rate}</Text>
                  <View style={{ width: 41.5, alignItems: 'center', paddingVertical: 4 }}>
                    <Text style={{ fontSize: 6 }}>Pcs.</Text>
                    <View style={{ width: 20, height: 1, backgroundColor: 'black', marginVertical: 4, alignSelf: 'center' }} />
                    <Text style={{ fontSize: 6 }}>gm.</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          <View style={{ flexDirection: 'row' }}>
            <View style={[styles.leftSection, { marginRight: 10 }]}>
              <View style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Setting</Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Dt</Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Tm </Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Start</Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Dt </Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Tm </Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Finish</Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Dt </Text>
                  <Text style={[styles.tableCell, { textAlign: 'left' }]}>Tm </Text>
                </View>
              </View>
            </View>
            <View style={styles.rightSection}>
              <View
                style={{
                  borderWidth: 1,
                  borderColor: '#000',
                  marginBottom: 5,
                }}>
                <View style={styles.tableRow}>
                  <View
                    style={{
                      flex: 1,
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      paddingVertical: 10,
                      paddingHorizontal: 5,
                      borderRightWidth: 1,
                      borderColor: '#000',
                    }}>
                    
                    {/* "Temp" text placed before the fraction */}
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ fontSize: 8, marginRight: 4 }}>Temp.</Text>

                      {/* "C" above the line, "min" below */}
                      <View style={{ alignItems: 'center' }}>
                        <Text style={{ fontSize: 8 }}>C</Text>
                        <View style={{ width: 15, height: 1, backgroundColor: '#000', marginVertical: 2 }} />
                        <Text style={{ fontSize: 8 }}>Min.</Text>
                      </View>
                    </View>

                  </View>
                </View>

              </View>
              <Text style={{ textAlign: 'center' }}>Production</Text>
            </View>
          </View>

          {/* Load Test Table */}
          <View style={[styles.table]}>
            <View style={styles.tableRow}>
              <Text
                style={{
                  flex: 2,
                  paddingVertical: 3,
                  paddingHorizontal: 5,
                  borderRightWidth: 1,
                  borderColor: '#000',
                  fontSize: 8,
                }}>
                LOAD TEST
              </Text>
              <Text style={styles.tableCell}>Dt.</Text>
              <Text style={styles.tableCell}>Time</Text>
              <Text style={styles.tableCell}>Wt.</Text>
              <Text style={styles.tableCell}>Bal.</Text>
              <Text style={styles.tableCellLast}>Name</Text>
            </View>
            {[...Array(8)].map((_, i) => (
              <View key={i} style={styles.tableRow}>
                <Text
                  style={{
                    flex: 2,
                    paddingVertical: 3,
                    paddingHorizontal: 5,
                    borderRightWidth: 1,
                    borderColor: '#000',
                    fontSize: 8,
                  }}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCellLast}></Text>
              </View>
            ))}
          </View>
        </View>
        {/* Right Section */}
        <View style={styles.rightSection}>
          {/* Packing Table */}
          <View>
            <Text style={styles.sectionTitle}>Packing</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tableCell}>Date</Text>
                <Text style={styles.tableCell}>Time</Text>
                <Text style={styles.tableCell}>Wt. Kg.</Text>
                <Text style={styles.tableCell}>Quantity</Text>
                <Text style={styles.tableCellLast}>Signature</Text>
              </View>
              {[...Array(4)].map((_, i) => (
                <View key={i} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{/* {date} */}</Text>
                  <Text style={styles.tableCell}>{/* {data.time} */}</Text>
                  <Text style={styles.tableCell}>{/* {data.wtReqd} */}</Text>
                  <Text style={styles.tableCell}>{/* {data.quantity} */}</Text>
                  <Text style={styles.tableCellLast}></Text>
                </View>
              ))}
              <View style={styles.tableRow}>
                <Text style={styles.tableCell}>Total</Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCell}></Text>
                <Text style={styles.tableCellLast}></Text>
              </View>
            </View>

            <View style={styles.barcodeSection}>
              <Text style={styles.sectionTitle}>Card No. Barcode</Text>
              <View style={styles.barcodeBox}>
                <Image style={styles.barcodeImage} source={{ uri: barcode }} />
                <Text style={styles.barcodeLabel}>Card No. {String(data.cardNo)}</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

const generateBarcodeDataUrl = async (cardNo) => {
  const value = String(cardNo);
  try {
    if (typeof makeBarcode === 'function') {
      const canvas = document.createElement('canvas');
      makeBarcode(canvas, value, {
        format: 'CODE128',
        displayValue: true,
        fontSize: 16,
        height: 60,
        margin: 8,
        width: 2,
        background: '#ffffff',
        lineColor: '#000000',
      });
      return canvas.toDataURL('image/png');
    }
  } catch (err) {
    console.error('JsBarcode failed, falling back to QR:', err);
  }
  // Fallback so PDF still opens if barcode lib fails
  return QRCode.toDataURL(value, { margin: 1, width: 200 });
};

const OrderPDF = () => {
  const location = useLocation();
  const formData = location.state;
  const [barcode, setBarcode] = useState(null);

  const formattedDate = formData?.date
    ? new Date(formData.date).toISOString().split('T')[0]
    : '';

  useEffect(() => {
    let cancelled = false;
    if (!formData?.cardNo) return undefined;

    (async () => {
      try {
        const url = await generateBarcodeDataUrl(formData.cardNo);
        if (!cancelled) setBarcode(url);
      } catch (err) {
        console.error('Error generating barcode:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData?.cardNo]);

  if (!formData) {
    return <div style={{ padding: 20 }}>No order data found for PDF.</div>;
  }

  if (!barcode) {
    return <div style={{ padding: 20 }}>Generating barcode...</div>;
  }

  return (
    <div style={{ width: '100%', height: '600px' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <PDFContent data={formData} date={formattedDate} barcode={barcode} />
      </PDFViewer>
    </div>
  );
};

export default OrderPDF;
